import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import toast from "react-hot-toast";

import { auth } from "../lib/api";
import { queryClient } from "../lib/queryClient";

/** The account an identity check answered with, as one comparable key — null when nobody is signed in. */
const identityOf = (user) => {
  if (!user) return null;
  const id = user._id || user.id;
  return id ? String(id) : `${user.userType || ""}|${user.email || ""}|${user.loginMobile || ""}`;
};

const SIGN_OUT_UNCONFIRMED =
  "You have been signed out on this device, but we could not confirm it with VVR. Please close this browser window, "
  + "or sign in and sign out again when you are connected.";

// ── Where this lives, and why ────────────────────────────────────────────────
// The template's §3 folder contract has no `context/` folder — a public marketing site has "almost
// none" of this kind of state. A login-gated portal does need exactly one piece of it, so the
// provider and its hook live together in `hooks/`, which the contract does list. No new top-level
// folder is introduced, and nothing else in the app holds auth state.

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // The second half of the `/auth/me` payload. It was fetched and discarded, so nothing could react
  // to a forced password change — and `requirePasswordChanged` 403s every portal read while it is
  // true, which left a newly provisioned customer looking at a portal where nothing loaded.
  const [mustChangePassword, setMustChangePassword] = useState(false);
  // The last account whose data may be in the query cache. Deliberately NOT reset by a failed identity check, so a
  // network blip between two accounts cannot hide the change; only a sign-out (which clears the cache) resets it.
  const cachedIdentity = useRef(null);

  // The server is the only authority on identity. Asked on every boot and full reload; no persisted
  // user object exists to go stale, so a revoked session cannot outlive its revocation.
  const check = useCallback(async () => {
    try {
      // `GET /auth/me` answers `{ user, mustChangePassword }` — the user is NESTED, not the payload
      // itself. Storing the envelope left `userType` undefined, so `isCustomer()` was false for
      // every account and ProtectedRoute refused every real customer as "a staff login".
      const { user: me, mustChangePassword: forced } = await auth.me();
      // A DIFFERENT account now holds the session (sign-in, activation or set-up over someone else's session on a
      // shared device): drop the previous account's cached data BEFORE the new identity renders, or its bookings and
      // balances would show instantly as fresh. The same account re-checking (after a password change) keeps its cache.
      const next = identityOf(me);
      if (cachedIdentity.current !== null && next !== cachedIdentity.current) queryClient.clear();
      if (next !== null) cachedIdentity.current = next;
      setUser(me);
      setMustChangePassword(Boolean(forced));
    } catch {
      setUser(null); // 401 -> anonymous, not an error state
      setMustChangePassword(false);
    } finally {
      setLoading(false); // ALWAYS runs — the three-state gate in ProtectedRoute depends on it
    }
  }, []);

  useEffect(() => {
    // FALSE POSITIVE, SUPPRESSED DELIBERATELY — same as the admin panel.
    // `react-hooks` v7 does not follow the call through the `useCallback`, so it cannot see that
    // `await auth.me()` is evaluated before `setUser` is ever invoked: no setState runs in this
    // effect's synchronous body, and the cascading render the rule guards against cannot occur.
    // Not restructured — this effect is the only thing that establishes identity, and `ProtectedRoute`
    // gates on the `loading` flag it clears; deferring it would widen the pre-session window.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    check();
  }, [check]);

  // Resolves `true` when the server confirmed the session ended, `false` when only this device was signed out. It never
  // rejects, so no caller can leave an unhandled failure — and the local sign-out happens either way, so a customer on a
  // shared device is never left looking signed in because the network is down.
  const signOut = useCallback(async () => {
    let confirmed = true;
    try {
      await auth.logout();
    } catch (err) {
      // A 401 means the session had already ended on the server: that IS the outcome asked for.
      confirmed = err?.response?.status === 401;
    }
    setUser(null);
    setMustChangePassword(false);
    // Wipe the cache. Without this, the next account to sign in on a shared device briefly sees the
    // previous customer's bookings and balances — cached data renders instantly, before any refetch.
    queryClient.clear();
    cachedIdentity.current = null;
    // Never claim the server-side revocation when it was not confirmed; no technical detail is shown.
    if (!confirmed) toast.error(SIGN_OUT_UNCONFIRMED, { id: "sign-out-unconfirmed", duration: 10000 });
    return confirmed;
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, mustChangePassword, signOut, refresh: check }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

/**
 * Is this account a customer login?
 *
 * `userType` is Module 1's frozen vocabulary (`constants/roles.js`: "staff" | "customer"). Staff
 * accounts authenticate fine here but belong in the admin panel, and every portal route would show
 * them their own empty customer record rather than anything useful — so the shell says so plainly
 * instead of rendering a confusing empty portal.
 */
export const isCustomer = (user) => Boolean(user) && user.userType === "customer";
